import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Input,
    Select,
    Option,
    Chip,
    IconButton,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Textarea,
    Switch,
    Alert,
    Spinner,
    Badge
} from "@material-tailwind/react";
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    ChartBarIcon,
    PlayIcon,
    MagnifyingGlassIcon,
    ArrowPathIcon,
    FunnelIcon
} from "@heroicons/react/24/outline";
import ReactSelect from 'react-select';
import axios from 'axios';

const APP_FONT_FAMILY = "'Nunito', ui-sans-serif, system-ui, sans-serif";

const MessageRules = ({ rootUrl, token }) => {
    // Create axios instance with custom config - memoized to prevent infinite re-renders
    const apiClient = useMemo(() => axios.create({
        baseURL: rootUrl,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    }), [rootUrl, token]);

    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRules, setTotalRules] = useState(0);

    // Filters
    const [activeOnly, setActiveOnly] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Dialogs
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showStatsDialog, setShowStatsDialog] = useState(false);
    const [showTestDialog, setShowTestDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const [selectedRule, setSelectedRule] = useState(null);
    const [ruleStats, setRuleStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);

    // Form data
    const [formData, setFormData] = useState({
        rule_name: '',
        description: '',
        match_type: 'contains',
        match_value: '',
        case_sensitive: false,
        target_plate_no: '',
        priority: 0,
        is_active: true,
        trans_type: '' // Add trans_type for auto-posting
    });

    // Transaction types for auto-posting
    const [transactionTypes, setTransactionTypes] = useState([]);
    const [transTypesLoading, setTransTypesLoading] = useState(false);

    // Test data
    const [testMessage, setTestMessage] = useState('');
    const [testResult, setTestResult] = useState(null);
    const [testLoading, setTestLoading] = useState(false);

    const matchTypeOptions = [
        { value: 'equals', label: 'Equals' },
        { value: 'contains', label: 'Contains' },
        { value: 'starts_with', label: 'Starts With' },
        { value: 'ends_with', label: 'Ends With' },
        { value: 'regex', label: 'Regular Expression' }
    ];

    const fetchRules = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: currentPage,
                limit: 10,
                active_only: activeOnly
            };

            const response = await apiClient.get('/api/message-rules', { params });

            if (response.data.success) {
                setRules(response.data.data);
                setTotalPages(response.data.pagination.pages);
                setTotalRules(response.data.pagination.total);
            }
        } catch (err) {
            setError('Failed to fetch message rules');
            console.error('Error fetching rules:', err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, activeOnly, apiClient]);

    // Fetch transaction types for auto-posting dropdown
    const fetchTransactionTypes = useCallback(async () => {
        console.log('🔄 Fetching transaction types...');
        try {
            setTransTypesLoading(true);
            console.log('📡 Making API call to:', '/payments/types/car');
            const response = await apiClient.get('/payments/types/car');

            console.log('📥 API Response:', response);
            console.log('📥 Response data:', response.data);

            // The payments/types/car endpoint returns an array directly, not wrapped in {success, data}
            if (Array.isArray(response.data)) {
                // Transform the data to match our expected format
                const transformedTypes = response.data.map(item => ({
                    type: item.type,
                    short_desc: item.short_desc,
                    long_desc: item.long_desc,
                    is_active: true // Assuming all returned types are active
                }));
                setTransactionTypes(transformedTypes);
                console.log('✅ Transaction types loaded:', transformedTypes.length, transformedTypes);
            } else {
                console.warn('⚠️ Unexpected response format for transaction types:', response.data);
                setTransactionTypes([]);
            }
        } catch (err) {
            console.error('❌ Error fetching transaction types:', err);
            console.error('❌ Error response:', err.response?.data);
            console.error('❌ Error status:', err.response?.status);
            console.error('❌ Error config:', err.config);
            setTransactionTypes([]);
        } finally {
            setTransTypesLoading(false);
            console.log('🏁 Transaction types loading finished');
        }
    }, [apiClient]);

    useEffect(() => {
        fetchRules();
        fetchTransactionTypes();
    }, [fetchRules, fetchTransactionTypes]);

    const handleCreateRule = async () => {
        try {
            setLoading(true);
            const response = await apiClient.post('/api/message-rules', {
                ...formData,
                created_by: 'current_user' // You might want to get this from auth context
            });

            if (response.data.success) {
                setSuccess('Rule created successfully');
                setShowCreateDialog(false);
                resetForm();
                fetchRules();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create rule');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateRule = async () => {
        try {
            setLoading(true);
            const response = await apiClient.put(`/api/message-rules/${selectedRule.id}`, formData);

            if (response.data.success) {
                setSuccess('Rule updated successfully');
                setShowEditDialog(false);
                resetForm();
                fetchRules();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update rule');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRule = async () => {
        try {
            setLoading(true);
            const response = await apiClient.delete(`/api/message-rules/${selectedRule.id}`);

            if (response.data.success) {
                setSuccess('Rule deleted successfully');
                setShowDeleteDialog(false);
                setSelectedRule(null);
                fetchRules();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete rule');
        } finally {
            setLoading(false);
        }
    };

    const fetchRuleStats = async (ruleId) => {
        try {
            setStatsLoading(true);
            const response = await apiClient.get(`/api/message-rules/${ruleId}/stats`);

            if (response.data.success) {
                setRuleStats(response.data.data);
            }
        } catch (err) {
            setError('Failed to fetch rule statistics');
        } finally {
            setStatsLoading(false);
        }
    };

    const handleTestRule = async () => {
        try {
            setTestLoading(true);
            const response = await apiClient.post(`/api/message-rules/${selectedRule.id}/test`, {
                test_message: testMessage
            });

            if (response.data.success) {
                setTestResult(response.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to test rule');
        } finally {
            setTestLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            rule_name: '',
            description: '',
            match_type: 'contains',
            match_value: '',
            case_sensitive: false,
            target_plate_no: '',
            priority: 0,
            is_active: true,
            trans_type: '' // Reset trans_type for auto-posting
        });
        setSelectedRule(null);
    };

    const openEditDialog = (rule) => {
        setSelectedRule(rule);
        setFormData({
            rule_name: rule.rule_name,
            description: rule.description || '',
            match_type: rule.match_type,
            match_value: rule.match_value,
            case_sensitive: rule.case_sensitive,
            target_plate_no: rule.target_plate_no,
            priority: rule.priority,
            is_active: rule.is_active,
            trans_type: rule.trans_type || '' // Include trans_type for auto-posting
        });
        setShowEditDialog(true);
    };

    const openStatsDialog = (rule) => {
        setSelectedRule(rule);
        setShowStatsDialog(true);
        fetchRuleStats(rule.id);
    };

    const openTestDialog = (rule) => {
        setSelectedRule(rule);
        setTestMessage('');
        setTestResult(null);
        setShowTestDialog(true);
    };

    const openDeleteDialog = (rule) => {
        setSelectedRule(rule);
        setShowDeleteDialog(true);
    };

    const toggleActiveOnlyFilter = () => {
        setCurrentPage(1);
        setActiveOnly((prev) => !prev);
    };

    const filteredRules = rules.filter(rule =>
        rule.rule_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rule.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rule.match_value.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rule.target_plate_no.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-3 sm:p-6 bg-[#fff8f5] min-h-screen font-sans">
            <div className="text-center mb-6">
                <Typography variant="h3" className="text-[#2a2e30] font-bold text-3xl sm:text-4xl mb-2">
                    Message Rules Management
                </Typography>
                <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full"></div>
            </div>

            {/* Alerts */}
            {error && (
                <Alert color="red" className="mb-4" onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert color="green" className="mb-4" onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            <div className="mb-4 sm:mb-8">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 sm:gap-4 mb-4 sm:mb-6 bg-white border border-gray-200 rounded-sm p-2 sm:p-3 shadow-none">
                    <div className="flex flex-wrap gap-2 sm:justify-start justify-end w-full sm:w-auto">
                        <Button
                            onClick={toggleActiveOnlyFilter}
                            variant="outlined"
                            size="sm"
                            className={`flex items-center gap-2 text-sm rounded-sm px-3 py-2 shadow-none border normal-case ${
                                activeOnly
                                    ? 'bg-[#FF5733] border-[#FF5733] text-white hover:bg-[#e64520]'
                                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200'
                            }`}
                        >
                            <FunnelIcon className="h-4 w-4" />
                            <span>{activeOnly ? 'Active Only On' : 'Active Only'}</span>
                        </Button>
                        <Button
                            onClick={fetchRules}
                            className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-3 py-2 shadow-none flex items-center gap-2 normal-case"
                            size="sm"
                        >
                            <ArrowPathIcon className="h-4 w-4" />
                            <span>Refresh</span>
                        </Button>
                        <Button
                            onClick={() => setShowCreateDialog(true)}
                            className="bg-brand-success hover:bg-brand-success/90 text-white text-sm font-semibold rounded-sm px-3 py-2 shadow-none flex items-center gap-2 normal-case"
                            size="sm"
                        >
                            <PlusIcon className="h-4 w-4" />
                            <span>Create Rule</span>
                        </Button>
                    </div>

                    <div className="w-full sm:w-[320px]">
                        <Input
                            placeholder="Search rules..."
                            icon={<MagnifyingGlassIcon className="h-4 w-4" />}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="text-sm"
                            containerProps={{ className: "min-w-0 rounded-sm" }}
                        />
                    </div>
                </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                <Typography variant="small" color="blue-gray" className="font-medium text-sm">
                    Showing {filteredRules.length} of {totalRules} rules
                </Typography>
            </div>

            {/* Rules Table */}
            <Card className="w-full overflow-hidden bg-white shadow-lg rounded-xl border border-gray-100">
                <CardBody className="overflow-x-auto p-0">
                    {loading ? (
                        <div className="flex justify-center items-center h-32">
                            <Spinner className="h-8 w-8" />
                        </div>
                    ) : filteredRules.length === 0 ? (
                        <div className="text-center py-8">
                            <Typography color="gray">No message rules found</Typography>
                        </div>
                    ) : (
                        <table className="w-full min-w-[920px] table-auto text-left">
                            <thead>
                                <tr className="border-b border-blue-gray-100 bg-[#EDE4DA]">
                                    <th className="p-2 w-[220px]">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                                            Rule Name
                                        </Typography>
                                    </th>
                                    <th className="p-2 w-[220px]">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                                            Match Condition
                                        </Typography>
                                    </th>
                                    <th className="p-2 w-[100px]">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                                            Target Plate
                                        </Typography>
                                    </th>
                                    <th className="p-2 w-[60px] text-center">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                                            Priority
                                        </Typography>
                                    </th>
                                    <th className="p-2 w-[60px] text-center">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                                            Hits
                                        </Typography>
                                    </th>
                                    <th className="p-2 w-[80px]">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                                            Status
                                        </Typography>
                                    </th>
                                    <th className="p-2 w-[90px]">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                                            Actions
                                        </Typography>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRules.map((rule) => (
                                    <tr key={rule.id} className="border-b border-blue-gray-50 hover:bg-blue-gray-50/50 transition-colors">
                                        <td className="p-2 align-top">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <Typography variant="small" color="blue-gray" className="font-medium text-xs">
                                                        {rule.rule_name}
                                                    </Typography>
                                                    {rule.trans_type && (
                                                        <Chip
                                                            size="sm"
                                                            variant="gradient"
                                                            value="🤖 Auto-Post"
                                                            color="green"
                                                            className="rounded-sm text-[10px]"
                                                        />
                                                    )}
                                                </div>
                                                {rule.description && (
                                                    <Typography variant="small" color="gray" className="text-xs">
                                                        {rule.description}
                                                    </Typography>
                                                )}
                                                {rule.trans_type && (
                                                    <Typography variant="small" color="green" className="text-xs font-medium">
                                                        Auto-posts as: {rule.trans_type}
                                                    </Typography>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-2 align-top">
                                            <div>
                                                <Chip
                                                    size="sm"
                                                    variant="ghost"
                                                    value={rule.match_type}
                                                    color="blue"
                                                    className="mb-1 rounded-sm text-[10px]"
                                                />
                                                <Typography variant="small" color="gray" className="font-mono text-xs">
                                                    "{rule.match_value}"
                                                </Typography>
                                                {rule.case_sensitive && (
                                                    <Chip size="sm" variant="outlined" value="Case Sensitive" color="orange" className="rounded-sm text-[10px]" />
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-2 align-top">
                                            <Typography variant="small" color="blue-gray" className="font-medium text-xs">
                                                {rule.target_plate_no}
                                            </Typography>
                                        </td>
                                        <td className="p-2 align-middle text-center">
                                            <Badge content={rule.priority} color="gray">
                                                <div className="w-6 h-6 mx-auto"></div>
                                            </Badge>
                                        </td>
                                        <td className="p-2 align-top text-center">
                                            <Typography variant="small" color="blue-gray" className="font-medium text-xs">
                                                {rule.total_hits || 0}
                                            </Typography>
                                        </td>
                                        <td className="p-2 align-top">
                                            <Chip
                                                size="sm"
                                                variant="ghost"
                                                value={rule.is_active ? "Active" : "Inactive"}
                                                color={rule.is_active ? "green" : "red"}
                                                className="rounded-sm text-[10px] w-fit"
                                            />
                                        </td>
                                        <td className="p-2 align-top">
                                            <div className="flex gap-2">
                                                <IconButton
                                                    variant="text"
                                                    size="sm"
                                                    onClick={() => openTestDialog(rule)}
                                                    title="Test Rule"
                                                    className="rounded-sm"
                                                >
                                                    <PlayIcon className="h-4 w-4" />
                                                </IconButton>
                                                <IconButton
                                                    variant="text"
                                                    size="sm"
                                                    onClick={() => openStatsDialog(rule)}
                                                    title="View Statistics"
                                                    className="rounded-sm"
                                                >
                                                    <ChartBarIcon className="h-4 w-4" />
                                                </IconButton>
                                                <IconButton
                                                    variant="text"
                                                    size="sm"
                                                    onClick={() => openEditDialog(rule)}
                                                    title="Edit Rule"
                                                    className="rounded-sm"
                                                >
                                                    <PencilIcon className="h-4 w-4" />
                                                </IconButton>
                                                <IconButton
                                                    variant="text"
                                                    size="sm"
                                                    color="red"
                                                    onClick={() => openDeleteDialog(rule)}
                                                    title="Delete Rule"
                                                    className="rounded-sm"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </IconButton>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardBody>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                    <Typography variant="small" color="gray" className="text-sm">
                        Page {currentPage} of {totalPages}
                    </Typography>
                    <div className="flex gap-2">
                        <Button
                            variant="outlined"
                            size="sm"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(currentPage - 1)}
                            className="rounded-sm shadow-none normal-case"
                        >
                            Previous
                        </Button>
                        <Button
                            variant="outlined"
                            size="sm"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(currentPage + 1)}
                            className="rounded-sm shadow-none normal-case"
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}

            {/* Create Rule Dialog */}
            <Dialog open={showCreateDialog} handler={() => setShowCreateDialog(false)} size="lg" className="font-sans">
                <DialogHeader className="font-sans">Create New Message Rule</DialogHeader>
                <DialogBody divider className="space-y-4 font-sans text-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                            label="Rule Name *"
                            value={formData.rule_name}
                            onChange={(e) => setFormData({ ...formData, rule_name: e.target.value })}
                        />
                        <Input
                            label="Target Plate Number *"
                            value={formData.target_plate_no}
                            onChange={(e) => setFormData({ ...formData, target_plate_no: e.target.value })}
                        />
                    </div>

                    <Textarea
                        label="Description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Select
                            label="Match Type *"
                            value={formData.match_type}
                            onChange={(value) => setFormData({ ...formData, match_type: value })}
                        >
                            {matchTypeOptions.map((option) => (
                                <Option key={option.value} value={option.value}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>

                        <Input
                            label="Priority"
                            type="number"
                            value={formData.priority}
                            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                        />
                    </div>

                    <Input
                        label="Match Value *"
                        value={formData.match_value}
                        onChange={(e) => setFormData({ ...formData, match_value: e.target.value })}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-blue-gray-700">
                                Transaction Type (Auto-Posting) {transTypesLoading ? '- Loading...' : `- ${transactionTypes.length} available`}
                            </label>
                            <ReactSelect
                                value={
                                    formData.trans_type
                                        ? { value: formData.trans_type, label: `${formData.trans_type} - ${transactionTypes.find(t => t.type === formData.trans_type)?.short_desc || ''}` }
                                        : null
                                }
                                onChange={(selectedOption) =>
                                    setFormData({ ...formData, trans_type: selectedOption ? selectedOption.value : '' })
                                }
                                options={[
                                    { value: '', label: 'Select Transaction Type (Optional)' },
                                    ...transactionTypes.map((type) => ({
                                        value: type.type,
                                        label: `${type.type} - ${type.short_desc}`,
                                    }))
                                ]}
                                placeholder="Search transaction types..."
                                isClearable={true}
                                isSearchable={true}
                                isDisabled={transTypesLoading}
                                className="w-full"
                                classNamePrefix="custom-select"
                                styles={{
                                    control: (provided, state) => ({
                                        ...provided,
                                        fontFamily: APP_FONT_FAMILY,
                                        border: '1px solid #b0bec5',
                                        borderRadius: '8px',
                                        padding: '4px',
                                        boxShadow: state.isFocused ? '0 0 0 2px rgba(33, 150, 243, 0.2)' : 'none',
                                        '&:hover': {
                                            borderColor: '#2196f3'
                                        }
                                    }),
                                    input: (provided) => ({
                                        ...provided,
                                        fontFamily: APP_FONT_FAMILY
                                    }),
                                    singleValue: (provided) => ({
                                        ...provided,
                                        fontFamily: APP_FONT_FAMILY
                                    }),
                                    placeholder: (provided) => ({
                                        ...provided,
                                        fontFamily: APP_FONT_FAMILY
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        fontFamily: APP_FONT_FAMILY
                                    }),
                                    option: (provided, state) => ({
                                        ...provided,
                                        fontFamily: APP_FONT_FAMILY,
                                        backgroundColor: state.isSelected ? '#2196f3' : state.isFocused ? '#e3f2fd' : 'white',
                                        color: state.isSelected ? 'white' : '#37474f'
                                    })
                                }}
                            />
                        </div>

                        <div className="flex items-center">
                            {formData.trans_type && (
                                <div className="p-3 bg-blue-50 rounded-lg text-sm">
                                    <div className="font-medium text-blue-800">🤖 Auto-Posting Enabled</div>
                                    <div className="text-blue-600">Messages matching this rule will automatically create transactions</div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <Switch
                            label="Case Sensitive"
                            checked={formData.case_sensitive}
                            onChange={(e) => setFormData({ ...formData, case_sensitive: e.target.checked })}
                        />
                        <Switch
                            label="Active"
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        />
                    </div>
                </DialogBody>
                <DialogFooter className="font-sans">
                    <Button variant="text" color="red" onClick={() => setShowCreateDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="gradient"
                        color="green"
                        onClick={handleCreateRule}
                        disabled={loading || !formData.rule_name || !formData.match_value || !formData.target_plate_no}
                    >
                        {loading ? <Spinner className="h-4 w-4" /> : "Create Rule"}
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Edit Rule Dialog */}
            <Dialog open={showEditDialog} handler={() => setShowEditDialog(false)} size="lg" className="font-sans">
                <DialogHeader className="font-sans">Edit Message Rule</DialogHeader>
                <DialogBody divider className="space-y-4 font-sans text-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                            label="Rule Name *"
                            value={formData.rule_name}
                            onChange={(e) => setFormData({ ...formData, rule_name: e.target.value })}
                        />
                        <Input
                            label="Target Plate Number *"
                            value={formData.target_plate_no}
                            onChange={(e) => setFormData({ ...formData, target_plate_no: e.target.value })}
                        />
                    </div>

                    <Textarea
                        label="Description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Select
                            label="Match Type *"
                            value={formData.match_type}
                            onChange={(value) => setFormData({ ...formData, match_type: value })}
                        >
                            {matchTypeOptions.map((option) => (
                                <Option key={option.value} value={option.value}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>

                        <Input
                            label="Priority"
                            type="number"
                            value={formData.priority}
                            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                        />
                    </div>

                    <Input
                        label="Match Value *"
                        value={formData.match_value}
                        onChange={(e) => setFormData({ ...formData, match_value: e.target.value })}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-blue-gray-700">
                                Transaction Type (Auto-Posting) {transTypesLoading ? '- Loading...' : `- ${transactionTypes.length} available`}
                            </label>
                            <ReactSelect
                                value={
                                    formData.trans_type
                                        ? { value: formData.trans_type, label: `${formData.trans_type} - ${transactionTypes.find(t => t.type === formData.trans_type)?.short_desc || ''}` }
                                        : null
                                }
                                onChange={(selectedOption) =>
                                    setFormData({ ...formData, trans_type: selectedOption ? selectedOption.value : '' })
                                }
                                options={[
                                    { value: '', label: 'Select Transaction Type (Optional)' },
                                    ...transactionTypes.map((type) => ({
                                        value: type.type,
                                        label: `${type.type} - ${type.short_desc}`,
                                    }))
                                ]}
                                placeholder="Search transaction types..."
                                isClearable={true}
                                isSearchable={true}
                                isDisabled={transTypesLoading}
                                className="w-full"
                                classNamePrefix="custom-select"
                                styles={{
                                    control: (provided, state) => ({
                                        ...provided,
                                        fontFamily: APP_FONT_FAMILY,
                                        border: '1px solid #b0bec5',
                                        borderRadius: '8px',
                                        padding: '4px',
                                        boxShadow: state.isFocused ? '0 0 0 2px rgba(33, 150, 243, 0.2)' : 'none',
                                        '&:hover': {
                                            borderColor: '#2196f3'
                                        }
                                    }),
                                    input: (provided) => ({
                                        ...provided,
                                        fontFamily: APP_FONT_FAMILY
                                    }),
                                    singleValue: (provided) => ({
                                        ...provided,
                                        fontFamily: APP_FONT_FAMILY
                                    }),
                                    placeholder: (provided) => ({
                                        ...provided,
                                        fontFamily: APP_FONT_FAMILY
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        fontFamily: APP_FONT_FAMILY
                                    }),
                                    option: (provided, state) => ({
                                        ...provided,
                                        fontFamily: APP_FONT_FAMILY,
                                        backgroundColor: state.isSelected ? '#2196f3' : state.isFocused ? '#e3f2fd' : 'white',
                                        color: state.isSelected ? 'white' : '#37474f'
                                    })
                                }}
                            />
                        </div>

                        <div className="flex items-center">
                            {formData.trans_type && (
                                <div className="p-3 bg-blue-50 rounded-lg text-sm">
                                    <div className="font-medium text-blue-800">🤖 Auto-Posting Enabled</div>
                                    <div className="text-blue-600">Messages matching this rule will automatically create transactions</div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <Switch
                            label="Case Sensitive"
                            checked={formData.case_sensitive}
                            onChange={(e) => setFormData({ ...formData, case_sensitive: e.target.checked })}
                        />
                        <Switch
                            label="Active"
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        />
                    </div>
                </DialogBody>
                <DialogFooter className="font-sans">
                    <Button variant="text" color="red" onClick={() => setShowEditDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="gradient"
                        color="blue"
                        onClick={handleUpdateRule}
                        disabled={loading || !formData.rule_name || !formData.match_value || !formData.target_plate_no}
                    >
                        {loading ? <Spinner className="h-4 w-4" /> : "Update Rule"}
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Test Rule Dialog */}
            <Dialog open={showTestDialog} handler={() => setShowTestDialog(false)} size="md" className="font-sans">
                <DialogHeader className="font-sans">Test Rule: {selectedRule?.rule_name}</DialogHeader>
                <DialogBody divider className="space-y-4 font-sans text-sm">
                    <Textarea
                        label="Test Message"
                        value={testMessage}
                        onChange={(e) => setTestMessage(e.target.value)}
                        placeholder="Enter a message to test against this rule..."
                    />

                    {testResult && (
                        <Card className={`${testResult.match_result ? 'border-green-500' : 'border-red-500'} border-2`}>
                            <CardBody>
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <Typography variant="h6">Test Result:</Typography>
                                        <Chip
                                            value={testResult.match_result ? "MATCH" : "NO MATCH"}
                                            color={testResult.match_result ? "green" : "red"}
                                        />
                                    </div>

                                    {testResult.match_result && (
                                        <Typography color="green">
                                            ✓ Message would be assigned to: <strong>{testResult.assigned_plate}</strong>
                                        </Typography>
                                    )}

                                    <div className="text-sm text-gray-600">
                                        <Typography variant="small">Match Type: {testResult.match_details.match_type}</Typography>
                                        <Typography variant="small">Match Value: "{testResult.match_details.match_value}"</Typography>
                                        <Typography variant="small">Case Sensitive: {testResult.match_details.case_sensitive ? 'Yes' : 'No'}</Typography>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    )}
                </DialogBody>
                <DialogFooter className="font-sans">
                    <Button variant="text" color="red" onClick={() => setShowTestDialog(false)}>
                        Close
                    </Button>
                    <Button
                        variant="gradient"
                        color="blue"
                        onClick={handleTestRule}
                        disabled={testLoading || !testMessage.trim()}
                    >
                        {testLoading ? <Spinner className="h-4 w-4" /> : "Test Rule"}
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Stats Dialog */}
            <Dialog open={showStatsDialog} handler={() => setShowStatsDialog(false)} size="lg" className="font-sans">
                <DialogHeader className="font-sans">Rule Statistics: {selectedRule?.rule_name}</DialogHeader>
                <DialogBody divider className="font-sans text-sm">
                    {statsLoading ? (
                        <div className="flex justify-center items-center h-32">
                            <Spinner className="h-8 w-8" />
                        </div>
                    ) : ruleStats ? (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <Card>
                                    <CardBody className="text-center">
                                        <Typography variant="h4" color="blue">
                                            {ruleStats.rule.total_hits || 0}
                                        </Typography>
                                        <Typography color="gray">Total Hits</Typography>
                                    </CardBody>
                                </Card>

                                <Card>
                                    <CardBody className="text-center">
                                        <Typography variant="h4" color="green">
                                            {ruleStats.daily_stats.length}
                                        </Typography>
                                        <Typography color="gray">Active Days</Typography>
                                    </CardBody>
                                </Card>

                                <Card>
                                    <CardBody className="text-center">
                                        <Typography variant="h4" color="purple">
                                            {ruleStats.recent_logs.length}
                                        </Typography>
                                        <Typography color="gray">Recent Logs</Typography>
                                    </CardBody>
                                </Card>
                            </div>

                            {ruleStats.rule.last_hit_at && (
                                <Typography color="gray">
                                    Last Hit: {new Date(ruleStats.rule.last_hit_at).toLocaleString()}
                                </Typography>
                            )}

                            {ruleStats.recent_logs.length > 0 && (
                                <div>
                                    <Typography variant="h6" className="mb-2">Recent Messages</Typography>
                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                        {ruleStats.recent_logs.map((log, index) => (
                                            <Card key={index} className="p-2">
                                                <Typography variant="small" className="font-mono">
                                                    {log.message_content}
                                                </Typography>
                                                <Typography variant="small" color="gray">
                                                    {new Date(log.processed_at).toLocaleString()} - {log.message_sender}
                                                </Typography>
                                            </Card>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <Typography color="gray">No statistics available</Typography>
                    )}
                </DialogBody>
                <DialogFooter className="font-sans">
                    <Button variant="text" color="blue" onClick={() => setShowStatsDialog(false)}>
                        Close
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} handler={() => setShowDeleteDialog(false)} size="sm" className="font-sans">
                <DialogHeader className="font-sans">Confirm Delete</DialogHeader>
                <DialogBody className="font-sans text-sm">
                    <Typography>
                        Are you sure you want to delete the rule "{selectedRule?.rule_name}"? This action cannot be undone.
                    </Typography>
                </DialogBody>
                <DialogFooter className="font-sans">
                    <Button variant="text" color="gray" onClick={() => setShowDeleteDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="gradient"
                        color="red"
                        onClick={handleDeleteRule}
                        disabled={loading}
                    >
                        {loading ? <Spinner className="h-4 w-4" /> : "Delete"}
                    </Button>
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default MessageRules;
